import React from "react";

import { useMutation } from "react-query-legacy";

import { Form, message } from "antd";
import { Store } from "antd/lib/form/interface";

import { BillingEngineService } from "services";

import FacilitiesList from "../_common/FacilitySelect";
import ServiceFormWrapper from "../_common/ServiceFormWrapper";
import ServiceRate from "../_common/ServiceRate";
import { useRateCardContext, useEditServiceContext } from "../contextProviders";
import { useCurrentVersion } from "../hooks";

import { serializedFormSchema, deserializedFormSchema } from "./formSchema";
import SameDayPostageFeeSection from "./SameDayPostageFeeSection";
import SpecialProjectsSection from "./SpecialProjectsSection";
import StorageSection from "./StorageSection";

const initialValues = {
  specialProjects: {
    price: 0,
    type: "hour",
  },
  sameDayPostageFee: 0,
  storage: {
    pallet: 0,
    doublePallet: 0,
    type: "week",
    shelf: 0,
    bin: 0,
  },
};

const WarehousingRateForm: React.FC = () => {
  const { setAllowEditing, isSaving, setIsSaving, isEditDisabled } = useEditServiceContext();

  // Current rate card
  const { rateCardId, rateCard, status, activeZone, dispatch, isLoadingZones } = useRateCardContext();
  const [form] = Form.useForm();

  // Creates version
  const [createVersion, { status: creatingVersionStatus }] = useMutation(
    BillingEngineService.updateZoneRateCardVersion,
    {
      onSuccess: (data) => {
        dispatch({
          type: "CREATE_VERSION",
          version: data,
        });
      },
    }
  );

  /**
   * Returns get version params
   * if false query will not run
   */
  const { data: currentVersion, status: currentVersionLoadingStatus } = useCurrentVersion(activeZone);

  const initialFormValues = currentVersion?.data || initialValues;

  /**
   * Handle form sumbition
   * @param values Form Values
   */
  const onFinish = (values: Store) => {
    setIsSaving(true);
    if (activeZone) {
      createVersion({
        rateCardId: activeZone.rateCardId,
        rateCardZoneId: activeZone.id,
        version: serializedFormSchema.cast(values),
      })
        .then(() => {
          message.success("New version created.");
        })
        .finally(() => {
          setAllowEditing(false);
          setIsSaving(false);
        });
    }
  };

  const onReset = () => {
    form.resetFields();
    const deserialized = deserializedFormSchema.cast(initialFormValues);
    if (deserialized) {
      form.setFieldsValue(deserialized);
    }
  };

  /**
   * Effect that handle form values populations
   * whenever one of dependencies changes
   */
  React.useEffect(() => {
    // Cleanup form every time
    if (form) {
      form.resetFields();
    }

    if (currentVersion && currentVersion.data) {
      const deserializedFormValues = deserializedFormSchema.cast(currentVersion.data);

      form.setFieldsValue(deserializedFormValues || initialValues);
    }
  }, [currentVersion, currentVersionLoadingStatus, rateCardId, form]);

  const isLoading =
    currentVersionLoadingStatus === "loading" ||
    creatingVersionStatus === "loading" ||
    status === "loading" ||
    isLoadingZones;
  const isDisableEdit = isEditDisabled || isLoading;

  return (
    <ServiceRate serviceName="Warehouse">
      <ServiceFormWrapper
        onSave={() => {
          form.submit();
        }}
        onCancel={onReset}
        loading={isLoading}
        title="Activated Facility"
        saving={isSaving}
        isEditDisabled={isDisableEdit}
      >
        {status === "success" && rateCard && <FacilitiesList />}
        <Form hideRequiredMark name="warehouse" form={form} onFinish={onFinish} initialValues={initialFormValues}>
          <SpecialProjectsSection />
          <SameDayPostageFeeSection />
          <StorageSection />
        </Form>
      </ServiceFormWrapper>
    </ServiceRate>
  );
};

export default WarehousingRateForm;
